import React from 'react'
import { Box, Button, Card, CardContent, CardHeader, Container, Grid, Typography, useTheme } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'

interface PricingPlan {
	title: string
	active?: boolean
	price: string
	oldPrice?: string
	period: string
	buttonText: string
	buttonLink?: string
	buttonVariant: 'outlined' | 'contained'
	description: string
	features: string[]
	mostPopular?: boolean
	recommended?: boolean
}

const plans: PricingPlan[] = [
	{
		title: 'Free',
		active: false,
		price: '$0',
		period: 'forever',
		buttonText: 'Coming this fall',
		buttonVariant: 'outlined',
		description: "For small teams who don't have the budget for end-to-end testing, but can't afford not to do it.",
		features: [
			'Functional smoke testing',
			'Custom rules & flows',
			'Custom test data',
			'Shared server (no SLA)',
			'500 test actions per day',
			'2 weeks of run history',
			'Email support'
		]
	},
	{
		title: 'Plus',
		active: true,
		price: '$0 *',
		oldPrice: '$150',
		period: 'per month',
		buttonText: 'Apply for early access',
		buttonLink: 'https://forms.office.com/r/rm048fwmxS',
		buttonVariant: 'outlined',
		description: 'For serious applications where functionality, performance, and accessibility are all important.',
		features: [
			'All Free features',
			'Accessibility testing',
			'Performance testing',
			'5,000 test actions per day',
			'2 weeks of run history',
			'Email support'
		],
		mostPopular: true
	},
	{
		title: 'Pro',
		active: false,
		price: '$800',
		period: 'per month',
		buttonText: 'Coming this fall',
		buttonVariant: 'outlined',
		description: 'For teams that test frequent builds of larger and more complex applications.',
		features: [
			'All Plus features',
			'Scheduled runs',
			'Concierge service',
			'Shared cluster with SLA',
			'50,000 test actions per day',
			'6 months of run history',
			'Phone support'
		]
	},
	{
		title: 'Enterprise',
		active: true,
		price: '$4300',
		period: 'per month',
		buttonText: 'Contact sales',
		buttonLink: './contact-us',
		buttonVariant: 'outlined',
		description: 'For organizations with custom needs and large-scale testing requirements.',
		features: [
			'All Pro features',
			'Unlimited test actions',
			'1 year of run history',
			'Concierge service',
			'Dedicated account manager',
			'Customization services (30 hrs/month)',
			'Phone support'
		],
		recommended: false
	}
]

const PricingTable: React.FC = () => {
	const theme = useTheme()
	return (
		<Box py={4}>
			<Container>
				<Grid container spacing={4} justifyContent="center">
					{plans.map(plan => (
						<Grid item key={plan.title} xs={12} sm={6} md={3}>
							<Card
								raised={plan.mostPopular || plan.recommended}
								sx={{
									height: '100%',
									display: 'flex',
									flexDirection: 'column',
									position: 'relative',
									transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
									'&:hover': {
										transform: 'translateY(-5px)',
										boxShadow: theme.shadows[10]
									},
									...(plan.mostPopular && {
										borderColor: theme.palette.primary.main,
										borderWidth: 2,
										borderStyle: 'solid'
									}),
									...(plan.recommended && {
										borderColor: theme.palette.secondary.main,
										borderWidth: 2,
										borderStyle: 'solid'
									})
								}}
							>
								{(plan.mostPopular || plan.recommended) && (
									<Box
										sx={{
											position: 'absolute',
											top: 0,
											right: 0,
											backgroundColor: plan.mostPopular ? theme.palette.primary.main : theme.palette.secondary.main,
											color: '#fff',
											padding: '4px 8px',
											borderBottomLeftRadius: 4,
											fontSize: '0.75rem',
											fontWeight: 'bold'
										}}
									>
										{plan.mostPopular ? 'MOST POPULAR' : 'RECOMMENDED'}
									</Box>
								)}
								<CardHeader
									title={plan.title}
									titleTypographyProps={{ align: 'center', variant: 'h4', fontWeight: 'bold' }}
									sx={{
										backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'
									}}
								/>
								<CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
									<Box sx={{ textAlign: 'center', mb: 2 }}>
										{plan.oldPrice && (
											<Typography
												variant="subtitle1"
												component="span"
												sx={{
													textDecoration: 'line-through',
													color: 'text.disabled',
													mr: 1,
													fontWeight: 'normal'
												}}
											>
												{plan.oldPrice}
											</Typography>
										)}
										<Typography variant="h3" component="div" sx={{ fontWeight: 'bold', display: 'inline' }}>
											{plan.price}
										</Typography>
										<Typography variant="body1" color={theme.palette.grey[600]}>
											{plan.period}
										</Typography>
									</Box>
									<Typography variant="body2" color="text.secondary" align="center" paragraph>
										{plan.description}
									</Typography>
									<Box sx={{ flexGrow: 1 }}>
										{plan.features.map(feature => (
											<Box key={feature} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
												<CheckIcon sx={{ color: theme.palette.success.main, mr: 1, fontSize: '1.2rem' }} />
												<Typography variant="body2">{feature}</Typography>
											</Box>
										))}
									</Box>
									<Button
										fullWidth
										variant={plan.buttonVariant}
										color={plan.mostPopular ? 'primary' : plan.recommended ? 'secondary' : 'primary'}
										sx={{ mt: 2 }}
										endIcon={<ArrowForwardIcon />}
										component={plan.buttonLink ? 'a' : 'button'}
										href={plan.buttonLink}
										target={plan.buttonLink ? '_blank' : undefined}
										rel={plan.buttonLink ? 'noopener noreferrer' : undefined}
										disabled={!plan.active}
									>
										{plan.buttonText}
									</Button>
								</CardContent>
							</Card>
						</Grid>
					))}
				</Grid>

				<Box mt={6} textAlign="center">
					<Typography variant="body1">
						All plans include our satisfaction guarantee: we will provide a full refund if ASTA does not deliver the value that
						you expected. <br />
						Concierge service includes workspace setup, one orientation session, and weekly test reports.
					</Typography>
					<Typography variant="body2" color="text.secondary" mt={2}>
						* Early access pricing available for qualified applications. Prices shown are for monthly billing: annual billing
						discounts are available.
					</Typography>{' '}
				</Box>
			</Container>
		</Box>
	)
}

export default PricingTable
